// Pagina LOGIN / REGISTRAZIONE
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Card, CardBody } from '@/components/ui/Card';
import { useAuthStore } from '@/store/authStore';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/i18n';
import { Mail, Lock, AlertCircle, Chrome, ArrowLeft, KeyRound } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

export function AuthPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { loginEmail, registerEmail, loginGoogle, resendConfirm, resetPwd, loading } = useAuthStore();
  const [mode, setMode] = useState<'login' | 'register' | 'forgot' | 'resend'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [info, setInfo] = useState<string | null>(null);

  // Se Supabase non è configurato, mostra solo bottone "continua offline"
  // (Nessuna informazione tecnica sull'app esposta)
  if (!isSupabaseConfigured()) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-brand-50 via-slate-50 to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-purple-950/40 p-6">
        <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center text-white text-3xl font-bold shadow-xl shadow-brand-500/30 mb-4 animate-scale-in">
          PN
        </div>
        <h1 className="text-xl font-bold bg-gradient-to-r from-brand-600 to-purple-600 bg-clip-text text-transparent">Pira Notes</h1>
        <Button className="mt-8" onClick={() => navigate('/notes')}>
          {t('auth.continue_offline')}
        </Button>
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setInfo(null);
    if (mode === 'login') {
      const r = await loginEmail(email.trim(), password);
      if (r.ok) {
        toast.success(t('auth.login_success'));
        navigate('/notes');
      } else {
        toast.error(r.message);
      }
    } else if (mode === 'register') {
      if (password.length < 6) {
        toast.error(t('auth.password_too_short'));
        return;
      }
      const r = await registerEmail(email.trim(), password);
      if (r.ok) {
        setInfo(t('auth.register_confirm') + ' ' + t('auth.register_confirm_full'));
        toast.success(t('auth.register_success'));
        setMode('login');
      } else {
        toast.error(r.message);
      }
    } else if (mode === 'forgot') {
      const r = await resetPwd(email.trim());
      toast[r.ok ? 'success' : 'error'](r.message);
      if (r.ok) setMode('login');
    } else if (mode === 'resend') {
      const r = await resendConfirm(email.trim());
      toast[r.ok ? 'success' : 'error'](r.message);
      if (r.ok) setMode('login');
    }
  }

  async function handleGoogle() {
  try {
    await loginGoogle();
      // Se siamo su mobile, il login è riuscito e possiamo navigare.
      // Su web, questa riga non verrà mai eseguita perché il browser viene reindirizzato.
      if (Capacitor.isNativePlatform()) {
        toast.success(t('auth.login_success'));
        navigate('/notes');
      }
      // Su web, non facciamo nulla: il redirect si occupa di tutto.
    } catch (e: any) {
      toast.error(e?.message ?? 'Errore Google login');
    }
  }

  const titleMap = {
    login: t('auth.login_title'),
    register: t('auth.register_title'),
    forgot: t('auth.forgot_title'),
    resend: t('auth.resend_title'),
  };
  const submitLabelMap = {
    login: t('auth.login_submit'),
    register: t('auth.register_submit'),
    forgot: t('auth.forgot_submit'),
    resend: t('auth.resend_submit'),
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-brand-50 via-slate-50 to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-purple-950/40">
      {/* Back button per tornare alla home */}
      <button
        onClick={() => navigate('/notes')}
        className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-400 bg-white/70 dark:bg-slate-800/70 backdrop-blur px-3 py-2 rounded-xl shadow-sm"
        style={{ marginTop: 'env(safe-area-inset-top)' }}
      >
        <ArrowLeft size={16} /> {t('common.back')}
      </button>

      {/* Header con logo */}
      <div className="pt-16 pb-8 flex flex-col items-center">
        <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center text-white text-3xl font-bold shadow-xl shadow-brand-500/30 animate-scale-in">
          PN
        </div>
        <h1 className="mt-4 text-2xl font-bold bg-gradient-to-r from-brand-600 to-purple-600 bg-clip-text text-transparent">
          Pira Notes
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{titleMap[mode]}</p>
      </div>

      <div className="flex-1 px-6 pb-8">
        <div className="mx-auto max-w-md">
          {/* Google button */}
          {mode === 'login' || mode === 'register' ? (
            <>
              <Button
                variant="outline"
                className="w-full h-12"
                onClick={handleGoogle}
                loading={loading}
              >
                <Chrome size={18} className="text-brand-600" />
                {t('auth.google')}
              </Button>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                <span className="text-xs text-slate-400">{t('auth.or')}</span>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
              </div>
            </>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label={t('auth.email')}>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.email_ph')}
                  className="pl-9"
                  autoComplete="email"
                />
              </div>
            </Field>

            {(mode === 'login' || mode === 'register') && (
              <Field label={t('auth.password')}>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.password_ph')}
                    className="pl-9"
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  />
                </div>
              </Field>
            )}

            {info && (
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900/40 p-3 text-xs text-emerald-700 dark:text-emerald-300 flex items-start gap-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{info}</span>
              </div>
            )}

            <Button type="submit" className="w-full h-12" loading={loading} disabled={!email.trim()}>
              {submitLabelMap[mode]}
            </Button>
          </form>

          {/* Link modalità */}
          <div className="mt-6 space-y-2 text-center text-sm">
            {mode === 'login' && (
              <>
                <p className="text-slate-500 dark:text-slate-400">
                  {t('auth.no_account')}{' '}
                  <button onClick={() => { setMode('register'); setInfo(null); }} className="font-semibold text-brand-600 hover:underline">
                    {t('auth.register_submit')}
                  </button>
                </p>
                <div className="flex items-center justify-center gap-4 text-xs text-slate-400">
                  <button onClick={() => { setMode('forgot'); setInfo(null); }} className="hover:text-brand-600 inline-flex items-center gap-1">
                    <KeyRound size={11} /> {t('auth.forgot_link')}
                  </button>
                  <button onClick={() => { setMode('resend'); setInfo(null); }} className="hover:text-brand-600">
                    {t('auth.resend_link')}
                  </button>
                </div>
              </>
            )}
            {mode === 'register' && (
              <p className="text-slate-500 dark:text-slate-400">
                {t('auth.have_account')}{' '}
                <button onClick={() => { setMode('login'); setInfo(null); }} className="font-semibold text-brand-600 hover:underline">
                  {t('auth.login_submit')}
                </button>
              </p>
            )}
          </div>

          {/* Info conferma email dopo registrazione */}
          {mode === 'register' && (
            <Card className="mt-6 bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/30">
              <CardBody className="!p-3">
                <div className="flex items-start gap-2 text-xs text-blue-700 dark:text-blue-300">
                  <Mail size={14} className="mt-0.5 shrink-0" />
                  <p>{t('auth.register_email_hint')}</p>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
